import { getFieldsHiddenByCriteria } from 'lib/fieldCriteria';
import { hasValue } from 'lib/fields';
import { FieldResult } from 'types/types';
import { pluralise } from 'lib/pluralise';
import { smartText } from 'lib/str';
import { arrayJoin, filterArrayEmpty, mapObjectValues } from 'lib/riskbase';
import {
  AseFieldInstance,
  AseSignatory,
  AseTplFieldField,
  AseTplFieldGroup,
  AssessmentResults,
  AssessmentSignatory,
} from './types';
import {
  AssessmentResults as AssetAssessmentResults,
  VisibleQuestionResults,
} from '../assets/typesAse';

export function getRepeatableFilterKey(
  groupId: string,
  listType: 'adding' | 'added',
): string {
  return `${listType}-${groupId}`;
}

export function returnValidationErrors({
  groups,
  signatories,
  requireAllAnswers,
  actions,
  controls,
  visibleQuestionResults,
  groupRequiredValues,
  signatoryResults,
}: {
  // Results for visible questions
  visibleQuestionResults: Record<string, VisibleQuestionResults>;
  requireAllAnswers: boolean;
  actions: AssessmentResults['actions'];
  controls: AssessmentResults['controls'];
  signatories: AssessmentSignatory[];
  signatoryResults: Record<string, AseSignatory | null>;
  groups: Record<string, AseTplFieldGroup>;
  groupRequiredValues: Record<string, number>;
}): {
  signatories: string;
  groups: string;
  questions: string;
  checklists: string;
  actions: string;
  controls: string;
  array: string[];
} {
  // Signatories
  const missingSignatoryNames = signatories
    .filter((s) => s.requireOnSubmit && !signatoryResults[s.id])
    .map((s) => s.name);
  const signatoriesError = pluralise(
    missingSignatoryNames.length,
    smartText(
      `"${arrayJoin(missingSignatoryNames, '", "', '" and "')}" signature is required`,
    ),
    smartText(
      `"${arrayJoin(missingSignatoryNames, '", "', '" and "')}" signatures are required`,
    ),
    '',
  );

  // Field groups
  const missingGroupValuesNames = Object.values(groups)
    .filter((group) => !!groupRequiredValues[group.id])
    .map((group) => group.name);
  const groupsError = pluralise(
    missingGroupValuesNames.length,
    smartText(
      `"${arrayJoin(missingGroupValuesNames, '", "', '" and "')}" has incomplete fields`,
    ),
    smartText(
      `"${arrayJoin(missingGroupValuesNames, '", "', '" and "')}" have incomplete fields`,
    ),
    '',
  );

  const incompleteActionCount = Object.values(actions).filter(
    (finding) => finding && !finding.description,
  ).length;

  const incompleteControlCount = Object.values(controls).filter(
    (finding) => finding && !finding.description,
  ).length;

  // Questions and checklists error
  const { unansweredQuestionsCount, unansweredChecklistsCount } = Object.values(
    visibleQuestionResults,
  ).reduce(
    (acc, { checklistItemIds, checklistItemResults, answerId }) => {
      if (checklistItemIds.length) {
        if (checklistItemIds.length !== checklistItemResults.length) {
          acc.unansweredChecklistsCount += 1;
        }
      } else if (!answerId) {
        acc.unansweredQuestionsCount += 1;
      }
      return acc;
    },
    {
      unansweredQuestionsCount: 0,
      unansweredChecklistsCount: 0,
    },
  );
  const questionErrors = pluralise(
    requireAllAnswers ? unansweredQuestionsCount : 0,
    '1 question is not answered',
    '{x} questions are not answered',
    '',
  );
  const checklistErrors = pluralise(
    requireAllAnswers ? unansweredChecklistsCount : 0,
    '1 checklist is incomplete',
    '{x} checklists are incomplete',
    '',
  );

  // Actions
  const actionsError = pluralise(
    incompleteActionCount,
    '1 action has no description',
    '{x} actions have no description',
    '',
  );

  // Controls
  const controlsError = pluralise(
    incompleteControlCount,
    '1 control has no description',
    '{x} controls have no description',
    '',
  );

  return {
    signatories: signatoriesError,
    groups: groupsError,
    questions: questionErrors,
    checklists: checklistErrors,
    actions: actionsError,
    controls: controlsError,
    array: [
      signatoriesError,
      groupsError,
      questionErrors,
      checklistErrors,
      actionsError,
      controlsError,
    ].filter(filterArrayEmpty),
  };
}

/**
 * Get the missing required results count for a field group
 * If the group is a repeatable then `existingRequiredResultsCountForGroup` is
 * the sum of all the instances in the group
 */
export function getFieldGroupRequiredResultsValuesCountFromValues({
  existingCount,
  groupCount,
  newCount,
}: {
  existingCount: number;
  newCount: number;
  groupCount: number;
}): number {
  if (newCount === existingCount) {
    return existingCount;
  }
  return groupCount + (newCount - existingCount);
}

export function getFieldInstanceRequiredValuesCount({
  instance,
  assetResults,
  group,
  fields,
}: {
  instance: AseFieldInstance | undefined;
  assetResults: Record<string, FieldResult>;
  group: AseTplFieldGroup;
  fields: Record<string, AseTplFieldField>;
}): number {
  if (
    !instance ||
    instance.action === 'delete' ||
    instance.action === 'remove'
  ) {
    return 0;
  }
  return getFieldGroupRequiredValuesCount({
    assetResults,
    results: instance.results,
    group,
    fields,
  });
}

export function getFieldGroupRequiredValuesCount({
  assetResults,
  results,
  group,
  fields,
}: {
  assetResults: Record<string, FieldResult>;
  results: Record<string, FieldResult>;
  group: AseTplFieldGroup;
  fields: Record<string, AseTplFieldField>;
}): number {
  if (!group.calculated.requiredFieldIds.length) {
    return 0;
  }
  const finalResults = {
    ...assetResults,
    ...Object.fromEntries(
      Object.entries(results).filter(
        ([, result]) => result.action !== 'delete',
      ),
    ),
  };
  const fieldIsHiddenByCriteria = group.calculated.fieldsHaveCriteria
    ? getFieldsHiddenByCriteria({
        fieldIds: group.fieldIds,
        fields,
        results: finalResults,
      })
    : {};
  return group.calculated.requiredFieldIds.filter(
    (fieldId) =>
      !hasValue(finalResults[fieldId]) && !fieldIsHiddenByCriteria[fieldId],
  ).length;
}

export const offlineAssetAssessmentResultsToAssessmentResults = (
  rawResults: AssetAssessmentResults,
): AssessmentResults => {
  return {
    ...rawResults,
    fieldInstanceChecks: rawResults.fieldInstanceChecks || {},
    questionComments: rawResults.questionComments || {},
    details: {
      ...rawResults.details,
      assessedBy: rawResults.details.assessedBy.map((assessedBy) => ({
        id: assessedBy.id,
        name: assessedBy.name,
        email: assessedBy.email,
        role: assessedBy.role || '',
      })),
      authorOrganisation: rawResults.details.authorOrganisation || {
        id: '',
        name: '',
      },
    },
    fieldInstances: mapObjectValues(
      rawResults.fieldInstances,
      offlineAssetFieldInstanceToAssessment,
    ),
    actions: Object.entries(rawResults.actions).reduce<
      AssessmentResults['actions']
    >((acc, [actionId, action]) => {
      acc[actionId] = action
        ? {
            ...action,
            cost: action.cost !== undefined ? action.cost : '',
            pin: action.pin !== undefined ? action.pin : null,
            reference: action.reference || '',
            customReference: action.customReference || '',
            fieldInstanceId: action.fieldInstanceId || null,
          }
        : null;
      return acc;
    }, {}),
    controls: Object.entries(rawResults.controls).reduce<
      AssessmentResults['controls']
    >((acc, [controlId, control]) => {
      acc[controlId] = control
        ? {
            ...control,
            fieldInstanceId: control.fieldInstanceId || null,
          }
        : null;
      return acc;
    }, {}),
    flags: {},
    flagHistory: [],
    signatories: rawResults.signatories || {},
  };
};

export function offlineAssetFieldInstanceToAssessment(
  fieldInstance: AssetAssessmentResults['fieldInstances'][string],
): AssessmentResults['fieldInstances'][string] {
  return {
    instanceId: fieldInstance.instanceId,
    groupId: fieldInstance.groupId,
    reference: fieldInstance.reference,
    results: fieldInstance.results,
    checks: fieldInstance.checks || {},
    action: fieldInstance.action,
    draftAutoId: fieldInstance.draftAutoId,
    // draft: fieldInstance.draft,
    autoId: fieldInstance.autoId,
    createdTs: fieldInstance.createdTs,
    updatedTs: fieldInstance.updatedTs,
  };
}
